// Login — email + 6-digit OTP. Real backend (not the prototype's setTimeout fakes).
//
// Step machine: email → otp → success. On success, redirect to / (or the
// `state.from` path RequireAuth saved before redirecting here).
import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { browserSupportsWebAuthn, WebAuthnError } from "@simplewebauthn/browser";

import { I } from "@/components/Icons";
import { useAuth } from "@/store/auth";

type Step = "email" | "otp" | "success";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SECONDS = 30;

export function Login() {
  const location = useLocation();
  const navigate = useNavigate();

  const status = useAuth((s) => s.status);
  const requestOtp = useAuth((s) => s.requestOtp);
  const verifyOtp = useAuth((s) => s.verifyOtp);

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState<string[]>(["", "", "", "", "", ""]);
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Resend cooldown ticker. Declared before the early return below so every
  // render runs the same hooks in the same order (react-hooks/rules-of-hooks).
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Focus the first OTP digit input as soon as it mounts (step transitions
  // to "otp"). The previous code used setTimeout(..., 100) inside submitEmail
  // to wait for the DOM, which races on slow renders and on resubmits where
  // step was already "otp". useEffect fires after the commit phase, so the
  // ref is guaranteed populated by the time we focus.
  useEffect(() => {
    if (step === "otp") {
      otpRefs.current[0]?.focus();
    }
  }, [step]);

  // If already authenticated, go straight to the dashboard.
  if (status === "authenticated") {
    const from = (location.state as { from?: { pathname: string } } | null)
      ?.from?.pathname;
    return <Navigate to={from || "/"} replace />;
  }

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(email)) {
      setError("Enter a valid work email.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await requestOtp(email);
      setStep("otp");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      // Focus handled by the useEffect on `step` above — no setTimeout race.
    } catch (err) {
      setError(otpErrorMessage(err, "Couldn’t send code. Try again."));
    } finally {
      setSubmitting(false);
    }
  }

  async function verify(code: string) {
    setSubmitting(true);
    setError("");
    try {
      await verifyOtp(email, code);
      setStep("success");
      setTimeout(() => navigate("/", { replace: true }), 600);
    } catch (err) {
      setError(otpErrorMessage(err, "Incorrect code. Try again."));
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  }

  function handleOtpInput(i: number, val: string) {
    if (val.length > 1) {
      const digits = val.replace(/\D/g, "").slice(0, 6).split("");
      const next: string[] = ["", "", "", "", "", ""];
      digits.forEach((d, idx) => (next[idx] = d));
      setOtp(next);
      const lastIdx = Math.min(digits.length, 5);
      otpRefs.current[lastIdx]?.focus();
      if (digits.length === 6) void verify(next.join(""));
      return;
    }
    if (!/^\d?$/.test(val)) return;
    const next = [...otp];
    next[i] = val;
    setOtp(next);
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
    if (next.every((d) => d) && next.join("").length === 6) {
      void verify(next.join(""));
    }
  }

  function handleOtpKey(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !otp[i] && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === "ArrowLeft" && i > 0) otpRefs.current[i - 1]?.focus();
    if (e.key === "ArrowRight" && i < 5) otpRefs.current[i + 1]?.focus();
  }

  async function resend() {
    if (cooldown > 0) return;
    setError("");
    setOtp(["", "", "", "", "", ""]);
    try {
      await requestOtp(email);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      otpRefs.current[0]?.focus();
    } catch (err) {
      setError(otpErrorMessage(err, "Couldn’t resend code."));
    }
  }

  return (
    <div className="auth-shell">
      <div className="aside">
        <div className="aside-grid" />
        <div className="row gap-12">
          <img
            src="/sphere-logo.png"
            alt=""
            className="brand-mark"
            aria-hidden="true"
          />
          <div className="stack-2">
            <div className="brand-name">SPHERE PAYOUTS</div>
            <div className="eyebrow">Payouts Control Plane</div>
          </div>
        </div>

        <div style={{ marginTop: 48, position: "relative", zIndex: 2 }}>
          {/* Illustrative — the login aside has no session yet, so we cannot
              pull real /v1/stats. Marked clearly so it does not masquerade
              as live telemetry to a first-time visitor. */}
          <div className="eyebrow">Sample · illustrative</div>
          <div className="stack-12" style={{ marginTop: 20 }}>
            <StatRow k="USD volume settled" v="$48.21M" delta="+4.2%" />
            <StatRow k="INR volume settled" v="₹312.4 Cr" delta="+8.6%" />
            <StatRow k="Active merchants" v="142" delta="+3" />
            <StatRow k="Connected bank rails" v="14" delta="2 new" muted />
          </div>
        </div>

        <div className="quote">
          “Capital that knows where it’s going moves slower than capital that
          doesn’t — but it arrives.”
        </div>
        <div className="quote-meta">— Field manual, Sphere operations · §1.4</div>
      </div>

      <div className="panel">
        {step === "email" && (
          <form onSubmit={submitEmail} className="stack-16" key="email">
            <div className="stack-6">
              <div className="eyebrow">Sign in</div>
              <h1 style={{ fontSize: 28, letterSpacing: "-0.02em" }}>
                Access your console
              </h1>
              <p className="dim" style={{ fontSize: 13, marginTop: 4 }}>
                We’ll send a six-digit code to your email. No passwords on this
                platform.
              </p>
            </div>

            <div className="field">
              <label className="label" htmlFor="login-email">
                Work email
              </label>
              <div className="input-group">
                <span className="addon left">
                  <I.Mail size={13} />
                </span>
                <input
                  id="login-email"
                  className="input"
                  autoFocus
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                />
              </div>
              {error && (
                <div style={{ color: "var(--neg)", fontSize: 12, marginTop: 4 }}>
                  {error}
                </div>
              )}
            </div>

            <button
              className="btn accent"
              type="submit"
              disabled={submitting}
              style={{ height: 38, justifyContent: "center" }}
            >
              {submitting ? "Sending…" : "Send sign-in code"}
              {!submitting && <I.ArrowRight size={13} />}
            </button>

            <div
              className="dim"
              style={{ fontSize: 11.5, lineHeight: 1.5, marginTop: 8 }}
            >
              By continuing you agree to Sphere Payouts’ Master Services Agreement and
              acknowledge that all payout activity is logged for audit. SSO
              available for enterprise accounts.
            </div>
          </form>
        )}

        {step === "otp" && (
          <div className="stack-16" key="otp">
            <div className="stack-6">
              <div className="eyebrow">Step 2 of 2</div>
              <h1 style={{ fontSize: 26, letterSpacing: "-0.02em" }}>
                Enter your sign-in code
              </h1>
              <p className="dim" style={{ fontSize: 13 }}>
                A 6-digit code was sent to{" "}
                <span className="mono" style={{ color: "var(--ink)" }}>
                  {email}
                </span>
                . It expires in 10 minutes.
              </p>
            </div>

            <div className="otp-row">
              {otp.map((v, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    otpRefs.current[i] = el;
                  }}
                  inputMode="numeric"
                  maxLength={i === 0 ? 6 : 1}
                  value={v}
                  className={v ? "filled" : ""}
                  onChange={(e) => handleOtpInput(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKey(i, e)}
                  aria-label={`Digit ${i + 1}`}
                />
              ))}
            </div>

            {error && (
              <div
                style={{ color: "var(--neg)", fontSize: 12, textAlign: "center" }}
              >
                {error}
              </div>
            )}
            {submitting && (
              <div
                className="dim"
                style={{ fontSize: 12, textAlign: "center" }}
              >
                Verifying…
              </div>
            )}

            <div
              className="row"
              style={{ justifyContent: "space-between", marginTop: 4 }}
            >
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => {
                  setStep("email");
                  setOtp(["", "", "", "", "", ""]);
                  setError("");
                }}
              >
                <I.ChevronLeft size={12} /> Use a different email
              </button>
              <button
                type="button"
                className="btn ghost sm"
                disabled={cooldown > 0}
                onClick={resend}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
              </button>
            </div>
          </div>
        )}

        {step === "success" && (
          <div
            className="stack-16 center"
            style={{ alignItems: "center", textAlign: "center" }}
          >
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                background: "var(--pos-soft)",
                color: "var(--pos)",
                display: "grid",
                placeItems: "center",
                margin: "0 auto",
              }}
            >
              <I.Check size={20} stroke={2} />
            </div>
            <h2 style={{ fontSize: 18 }}>Welcome back</h2>
            <div className="dim" style={{ fontSize: 13 }}>
              Loading your console…
            </div>
          </div>
        )}

        {step !== "success" && <PasskeyLoginButton onError={setError} />}

        <div
          style={{
            marginTop: 56,
            display: "flex",
            justifyContent: "space-between",
            fontSize: 11,
            color: "var(--ink-4)",
          }}
        >
          <span>Sphere Payouts · payouts-panel</span>
          <span>v0.0.1</span>
        </div>
      </div>
    </div>
  );
}

function StatRow({
  k,
  v,
  delta,
  muted,
}: {
  k: string;
  v: string;
  delta: string;
  muted?: boolean;
}) {
  return (
    <div
      className="row"
      style={{
        justifyContent: "space-between",
        borderBottom: "1px solid var(--hair)",
        paddingBottom: 12,
        position: "relative",
        zIndex: 2,
      }}
    >
      <div className="stack-2">
        <div className="eyebrow">{k}</div>
        <div className="num" style={{ fontSize: 20, letterSpacing: "-0.01em" }}>
          {v}
        </div>
      </div>
      <div
        className={"num " + (muted ? "dim-2" : "")}
        style={{
          fontSize: 11,
          color: muted ? "var(--ink-4)" : "var(--pos)",
        }}
      >
        {delta}
      </div>
    </div>
  );
}

function otpErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const code = (err.response?.data as { code?: string } | undefined)?.code;
    if (code) return `Sign-in failed (${code}).`;
  }
  return fallback;
}

// PasskeyLoginButton — "Sign in with a passkey" under the OTP form. Runs the
// discoverable WebAuthn ceremony via the store, then mirrors the OTP success
// path (navigate to the saved `from` or /). Hides itself when the browser
// lacks WebAuthn support so users are never offered a control that can't work.
function PasskeyLoginButton({ onError }: { onError: (m: string) => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const loginWithPasskey = useAuth((s) => s.loginWithPasskey);

  // Resolve support after mount only — reading it during render is fine in a
  // pure SPA, but keeping it in state matches the reference and avoids any
  // hydration-timing surprises.
  const [supported, setSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setSupported(browserSupportsWebAuthn());
  }, []);

  if (!supported) return null;

  async function handleClick() {
    onError("");
    setLoading(true);
    try {
      await loginWithPasskey();
      const from = (location.state as { from?: { pathname: string } } | null)
        ?.from?.pathname;
      navigate(from || "/", { replace: true });
    } catch (err) {
      onError(passkeyErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="row" style={{ alignItems: "center", gap: 12, margin: "20px 0" }}>
        <div style={{ height: 1, flex: 1, background: "var(--hair)" }} />
        <span className="eyebrow">or</span>
        <div style={{ height: 1, flex: 1, background: "var(--hair)" }} />
      </div>
      <button
        type="button"
        className="btn"
        onClick={() => void handleClick()}
        disabled={loading}
        style={{ width: "100%", height: 38, justifyContent: "center" }}
      >
        <I.Keys size={13} />
        {loading ? "Waiting for passkey…" : "Sign in with a passkey"}
      </button>
    </>
  );
}

// passkeyErrorMessage maps a WebAuthn ceremony / transport failure to a
// user-facing string. A cancelled or dismissed OS prompt (NotAllowedError /
// AbortError / ERROR_CEREMONY_ABORTED) is not a real error — surface a calm
// "cancelled" rather than a scary failure.
function passkeyErrorMessage(err: unknown): string {
  if (
    (err instanceof WebAuthnError && err.code === "ERROR_CEREMONY_ABORTED") ||
    (err instanceof DOMException &&
      (err.name === "NotAllowedError" || err.name === "AbortError"))
  ) {
    return "Passkey sign-in was cancelled.";
  }
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 501) {
      return "Passkeys are not enabled on this server.";
    }
    if (err.response?.status === 429) {
      return "Too many attempts. Please wait a moment and try again.";
    }
    if (err.response?.status === 401) {
      return "That passkey was not recognised. Please try again.";
    }
    if (err.response?.status === 403) {
      return "You do not have access to this portal.";
    }
  }
  return "Passkey sign-in failed. Please try again.";
}
